/**
 * Theme Definitions
 * 
 * Contains the three theme palettes (peach, greenish, violetBluish) with complete color definitions.
 * All colors are specified as hex codes and meet WCAG AA contrast requirements (≥ 4.5:1 for normal text).
 * 
 * @module theme/themes
 */

import type { Theme, ThemeName } from './types';

/**
 * Peach Theme - Warm, playful aesthetic
 * 
 * Primary: Coral accent (#FF9193)
 * Background: Light peach (#FFC79A)
 * Text: Dark brown (#2A1B16)
 */
export const peachTheme: Theme = {
  name: 'peach',
  palette: {
    background: '#FFC79A',    // Light peach - main page background
    surface: '#FFE4CC',       // Lighter peach - cards/panels
    surfaceAlt: '#FFD9B3',    // Mid peach - hover states for surface elements
    accent: '#FF9193',        // Coral - primary buttons, links
    accentAlt: '#FF7A7C',     // Darker coral - hover/active states
    textPrimary: '#2A1B16',   // Dark brown - main text content
    textSecondary: '#5C4A42', // Medium brown - supporting text
    textOnAccent: '#2A1B16',  // Dark brown - text on coral buttons (WCAG AA compliant)
    border: '#EEBF99',        // Muted peach - dividers, borders
    focusRing: '#FF9193',     // Coral - focus indicators (matches accent)
    // Footer gradient: Light peach → Coral
    footerGradientStart: '#FFC79A',  // Matches background
    footerGradientEnd: '#FF9193',    // Matches accent
  },
};

/**
 * Greenish Theme - Natural, calming aesthetic
 * 
 * Primary: Olive accent (#93B177)
 * Background: Light sage (#DEECD0)
 * Text: Dark forest green (#1E2A17)
 */
export const greenishTheme: Theme = {
  name: 'greenish',
  palette: {
    background: '#DEECD0',    // Light sage - main page background
    surface: '#F0F7E8',       // Lighter sage - cards/panels
    surfaceAlt: '#E7F2D9',    // Mid sage - hover states for surface elements
    accent: '#93B177',        // Olive - primary buttons, links
    accentAlt: '#7FA05F',     // Darker olive - hover/active states
    textPrimary: '#1E2A17',   // Dark forest green - main text content
    textSecondary: '#3F5436', // Medium forest green - supporting text
    textOnAccent: '#1E2A17',  // Dark forest green - text on olive buttons (WCAG AA compliant)
    border: '#C9DCBC',        // Muted sage - dividers, borders
    focusRing: '#93B177',     // Olive - focus indicators (matches accent)
    // Footer gradient: Light sage → Olive
    footerGradientStart: '#DEECD0',  // Matches background
    footerGradientEnd: '#93B177',    // Matches accent
  },
};

/**
 * Violet Bluish Theme - Cool, sophisticated aesthetic
 * 
 * Primary: Dark violet accent (#5654BF)
 * Background: Light violet (#C5CBFF)
 * Text: Very dark blue (#14133B)
 */
export const violetBluishTheme: Theme = {
  name: 'violetBluish',
  palette: {
    background: '#C5CBFF',    // Light violet - main page background
    surface: '#E6E9FF',       // Lighter violet - cards/panels
    surfaceAlt: '#D5DAFF',    // Mid violet - hover states for surface elements
    accent: '#5654BF',        // Dark violet - primary buttons, links
    accentAlt: '#423FA8',     // Darker violet - hover/active states
    textPrimary: '#14133B',   // Very dark blue - main text content
    textSecondary: '#3B3A66', // Medium blue - supporting text
    textOnAccent: '#FFFFFF',  // White - text on violet buttons
    border: '#B5BBF5',        // Muted violet - dividers, borders
    focusRing: '#5654BF',     // Dark violet - focus indicators (matches accent)
    // Footer gradient: Light violet → Dark violet
    footerGradientStart: '#C5CBFF',  // Matches background
    footerGradientEnd: '#5654BF',    // Matches accent
  },
};

/**
 * Pacific Blue Theme - Fresh, modern aesthetic (Daily Sparks)
 * 
 * Primary: Cyan accent (#01A7C8)
 * Background: Light cyan (#E5F7FA)
 * Text: Dark gray-blue (#1B2831)
 */
export const pacificBlueTheme: Theme = {
  name: 'pacificBlue',
  palette: {
    background: '#E5F7FA',    // Light cyan - main page background
    surface: '#FFFFFF',       // White - cards/panels
    surfaceAlt: '#D6F0F5',    // Lighter cyan - hover states for surface elements
    accent: '#01A7C8',        // Cyan - primary buttons, links
    accentAlt: '#008CAA',     // Darker cyan - hover/active states
    textPrimary: '#1B2831',   // Dark gray-blue - main text content
    textSecondary: '#4D5762', // Medium gray - supporting text
    textOnAccent: '#FFFFFF',  // White - text on cyan buttons
    border: '#B8E4ED',        // Muted cyan - dividers, borders
    focusRing: '#01A7C8',     // Cyan - focus indicators (matches accent)
    // Footer gradient: Light cyan → Cyan
    footerGradientStart: '#E5F7FA',  // Matches background
    footerGradientEnd: '#01A7C8',    // Matches accent
  },
};

/**
 * Default theme used on initial load.
 * Can be overridden by localStorage value 'app.theme'.
 */
export const DEFAULT_THEME = pacificBlueTheme;

/**
 * Theme registry mapping theme names to theme objects.
 * Used for runtime theme switching and validation.
 */
export const themes: Record<ThemeName, Theme> = {
  peach: peachTheme,
  greenish: greenishTheme,
  violetBluish: violetBluishTheme,
  pacificBlue: pacificBlueTheme,
};
